import { Circle as CircleStyle, Icon, Stroke, Style } from "ol/style"
import { Feature } from "ol"
import { Point } from "ol/geom"
import { fromLonLat } from "ol/proj"
import { labelStyle } from "./label"

// EPSG:3857 resolution at zoom 15 — resolutions below it (zoom > 15) get labels.
// 156543.03… = world circumference 40075016.69m / 256px tile = m/px at zoom 0, halving per zoom.
const LABEL_MAX_RESOLUTION = 156543.03392804097 / 2 ** 15

export class Device {
    feature
    positionAt
    topics = new Set()
    #payload
    #offlineTimer
    #groupVisible = true
    #stale = false
    #highlighted = false
    #plainStyle
    #labeledStyle

    constructor(payload) {
        this.#payload   = payload
        this.feature    = new Feature({ geometry: new Point(fromLonLat([payload.lon, payload.lat])) })
        this.feature.set("device", this)
        this.positionAt = new Date(payload.position_at)
        // style function: pure lookup between cached variants — no allocation per render frame
        this.feature.setStyle((_, resolution) => this.#styleFor(resolution))
        this.#refreshStyle()
        this.#scheduleOfflineFlip()
    }

    get id()           { return this.#payload.id }
    get filterGroup()  { return this.#payload.filter_group }
    get name()      { return this.#payload.name }
    get issi()      { return this.#payload.issi }
    get status()    { return this.#payload.status }
    get members()   { return this.#payload.members ?? [] }
    get markerUrl() { return this.#payload.marker_url || Device.FALLBACK_MARKER_URL }

    applyFull(payload) {
        this.#payload   = payload
        this.feature.setGeometry(new Point(fromLonLat([payload.lon, payload.lat])))
        this.positionAt = new Date(payload.position_at)
        this.#refreshStyle()
        this.#scheduleOfflineFlip()
    }

    addTopic(topic)    { if (topic) this.topics.add(topic) }
    removeTopic(topic) { this.topics.delete(topic); return this.topics.size === 0 }
    setTopics(topics)  { this.topics = new Set(topics || []) }

    dispose() { clearTimeout(this.#offlineTimer) }

    get visible() { return (this.#groupVisible && !this.#stale) || this.#highlighted }

    setGroupVisibility(visible) { this.#groupVisible = visible; this.#refreshStyle() }
    setStale(stale) { if (this.#stale === stale) return; this.#stale = stale; this.#refreshStyle() }
    setHighlighted(highlighted) { this.#highlighted = highlighted; this.#refreshStyle() }

    #styleFor(resolution) {
        if (!this.visible) return null
        return resolution < LABEL_MAX_RESOLUTION ? this.#labeledStyle : this.#plainStyle
    }

    #refreshStyle() {
        const icon = new Style({ image: this.#icon() })
        this.#plainStyle = this.#highlighted ? [Device.HIGHLIGHT_STYLE, icon] : [icon]
        if (this.visible) {
            const label = this.#labelStyle()
            this.#labeledStyle = this.#highlighted ? [Device.HIGHLIGHT_STYLE, icon, label] : [icon, label]
        }
        this.feature.changed()
    }

    static FALLBACK_MARKER_URL = "/tracker/images/marker-icon.png"
    static HIGHLIGHT_STYLE = new Style({ image: new CircleStyle({ radius: 22, stroke: new Stroke({ color: "#0d6efd", width: 3 }) }) })

    #icon() {
        const offline = new Date(this.#payload.offline_at) <= new Date()
        const src = offline
            ? (this.#payload.offline_marker_url || Device.FALLBACK_MARKER_URL)
            : (this.#payload.marker_url || Device.FALLBACK_MARKER_URL)
        return new Icon({
            src: src,
            scale: 0.5,
            anchor: [0.5, 1],
            opacity: offline ? 0.5 : 1
        })
    }

    #labelStyle() {
        const iconUrl = this.filterGroup ? `https://pics.lsz-b.at/icons/${this.filterGroup}_c.png` : null
        return labelStyle({ name: this.name ?? "", status: this.#payload.status, iconUrl: iconUrl }, () => this.#refreshStyle())
    }

    #scheduleOfflineFlip() {
        clearTimeout(this.#offlineTimer)
        const ms = new Date(this.#payload.offline_at) - new Date()
        if (ms <= 0) return
        this.#offlineTimer = setTimeout(() => this.#refreshStyle(), ms + 500)
    }
}
